import React, { useCallback, useEffect, useState } from "react"
import { Box, Typography } from "@carrotfertility/carotene-core"
import { useFormContext, useWatch } from "@carrotfertility/carotene-core-x"
import { StepOptionSelect } from "./StepOptionSelect"
import {
  MediaOptionFields,
  CarrotPlanFormOption,
  MenuItemOptionFields,
  TopicOptionFields,
  FormResponsiveDisplayStatus
} from "./hooks/useCarrotPlanMappings"
import { carrotPlanMediaMenuItemIds, getCarrotPlanMediaReplacementForMenuItemId } from "./MediaTypes"
import { ContentfulItemDropdowns } from "./CarrotPlanContentfulDropdowns"
import { CarrotPlanFormStep, FieldName } from "./CarrotPlanFormTypes"
import { CarrotPlanLinkInput } from "./CarrotPlanLinkInput"
import { DragIndicator } from "@mui/icons-material"
import { getAutocompleteTopicOptions } from "./utils/autocompleteTransformations"

type CarrotPlanStepProps = {
  stepIndex: number
  inReview?: boolean
  options: CarrotPlanFormOption<MenuItemOptionFields>[]
  topicOptions: CarrotPlanFormOption<TopicOptionFields>[]
}

export type ItemType = "menuItem" | "content"

type AddDisabledMessageToDuplicatesProps<T extends MenuItemOptionFields | MediaOptionFields> = {
  options: CarrotPlanFormOption<T>[]
  steps: CarrotPlanFormStep[]
  stepIndex: number
  itemType: ItemType
}

/**
 * Adds a disabled message to items that are already added to the same plan
 */
export function addDisabledMessageToDuplicates<T extends MenuItemOptionFields | MediaOptionFields>({
  options,
  steps,
  stepIndex,
  itemType
}: AddDisabledMessageToDuplicatesProps<T>): CarrotPlanFormOption<T>[] {
  // check if the value is a media menu item because they can be duplicated
  const isMediaMenuItem = (value: string) => itemType === "menuItem" && carrotPlanMediaMenuItemIds.includes(value)
  // filter out the current step from the steps array so that we don't disable the current selection
  const filteredSteps = steps?.filter(function (_, index) {
    return index !== stepIndex
  })
  // get the selected itemType values from the filtered steps
  const selectedItems = filteredSteps?.map((step) => step?.[itemType])
  // map over the options and add a disabled message to the duplicates
  const updatedOptions = options?.map((option) => {
    const { value, optionMetadata, ...rest } = option
    const matchesAnotherSelection = selectedItems?.filter(Boolean).some((item) => item.value === value)
    if (!isMediaMenuItem(value) && matchesAnotherSelection) {
      return {
        ...rest,
        value,
        optionMetadata: {
          ...optionMetadata,
          displayStatus: FormResponsiveDisplayStatus.DUPLICATE_ON_SAME_PLAN
        }
      }
    }
    return option
  })
  return updatedOptions
}

/**
 * Clears the content field when the corresponding topic field is cleared
 */
function useResetOnChange({ watchedState, name }: { watchedState: string; name: string }) {
  const watchedValue = useWatch({ name: watchedState })
  const { setValue, clearErrors } = useFormContext()

  useEffect(() => {
    if (!watchedValue) {
      setValue(name, null)
      clearErrors(name)
      clearErrors(watchedState)
    }
  }, [name, setValue, watchedState, watchedValue, clearErrors])
}

/**
 * On a *user-initiated* menu item change, resets the url to the new item's default
 * and clears the topic (content cascades via `useResetOnChange`). Gated on
 * `userChangedMenuItem` so programmatic `setValue`s (reorder, journey swap) don't fire.
 */
function useResetDependentFieldsOnMenuItemChange({
  watchedMenuItem,
  urlField,
  topicField,
  userChangedMenuItem,
  setUserChangedMenuItem
}: {
  watchedMenuItem: string
  urlField: string
  topicField: string
  userChangedMenuItem: boolean
  setUserChangedMenuItem: (newValue: boolean) => void
}): void {
  const watchedValue = useWatch({ name: watchedMenuItem })
  const { setValue, clearErrors } = useFormContext()
  const newDefaultUrl = watchedValue?.optionFields?.defaultUrl

  useEffect(() => {
    if (userChangedMenuItem) {
      setValue(urlField, newDefaultUrl)
      setValue(topicField, null)
      clearErrors(urlField)
      clearErrors(topicField)
      clearErrors(watchedMenuItem)
      setUserChangedMenuItem(false)
    }
  }, [
    watchedMenuItem,
    userChangedMenuItem,
    setValue,
    urlField,
    topicField,
    newDefaultUrl,
    clearErrors,
    setUserChangedMenuItem
  ])
}

export function CarrotPlanStep({
  stepIndex,
  inReview = false,
  options,
  topicOptions
}: CarrotPlanStepProps): JSX.Element {
  const createInputNameWithPrefix = (name: string) => `${FieldName.steps}.${stepIndex}.${name}`

  const [userChangedMenuItem, setUserChangedMenuItem] = useState(false)

  const [steps, menuItemSelection, contentSelection, topicSelection, stepUrl] = useWatch({
    name: [
      FieldName.steps,
      createInputNameWithPrefix(FieldName.menuItem),
      createInputNameWithPrefix(FieldName.content),
      createInputNameWithPrefix(FieldName.topic),
      createInputNameWithPrefix(FieldName.url)
    ]
  })

  useResetOnChange({
    watchedState: createInputNameWithPrefix(FieldName.topic),
    name: createInputNameWithPrefix(FieldName.content)
  })

  useResetDependentFieldsOnMenuItemChange({
    watchedMenuItem: createInputNameWithPrefix(FieldName.menuItem),
    urlField: createInputNameWithPrefix(FieldName.url),
    topicField: createInputNameWithPrefix(FieldName.topic),
    userChangedMenuItem,
    setUserChangedMenuItem
  })

  /*
   * Adds a disabled message to the options that are already added to the same plan
   */
  const getUpdatedStepOptionsInSelect = useCallback(
    function <T extends MenuItemOptionFields | MediaOptionFields>(
      options: CarrotPlanFormOption<T>[],
      itemType: ItemType
    ): CarrotPlanFormOption<T>[] {
      return addDisabledMessageToDuplicates<T>({
        options,
        steps: steps,
        stepIndex: stepIndex,
        itemType
      })
    },
    [steps, stepIndex]
  )

  const showContentfulDropdowns =
    inReview && menuItemSelection && carrotPlanMediaMenuItemIds.includes(menuItemSelection?.value)
  const showGenericLinkInput =
    inReview && menuItemSelection && !carrotPlanMediaMenuItemIds.includes(menuItemSelection?.value)

  return (
    <Box
      display="flex"
      gap={(theme) => theme.spacing(theme.tokens.spacing.sm)}
      justifyContent="space-around"
      alignItems="flex-start"
    >
      <Box display="flex" flex="1 0" gap={(theme) => theme.spacing(theme.tokens.spacing.sm)}>
        <Typography minWidth="20px">{stepIndex + 1}.</Typography>
        <DragIndicator fontSize="large" sx={{ color: "lightgray", cursor: "grab" }} />
        <Box display="flex" flexDirection="column" width="100%" gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
          <StepOptionSelect
            name={createInputNameWithPrefix(FieldName.menuItem)}
            options={getUpdatedStepOptionsInSelect<MenuItemOptionFields>(options, FieldName.menuItem)}
            disabled={inReview}
            placeholder="Select step..."
            onUserChange={() => setUserChangedMenuItem(true)}
            confirmRemovalOfMandatory
          />
          {showContentfulDropdowns && (
            <ContentfulItemDropdowns
              options={getAutocompleteTopicOptions(menuItemSelection?.value, topicOptions)}
              topicSelection={topicSelection}
              topicName={createInputNameWithPrefix(FieldName.topic)}
              contentName={createInputNameWithPrefix(FieldName.content)}
              contentSelection={contentSelection}
              replacement={getCarrotPlanMediaReplacementForMenuItemId(menuItemSelection?.value)}
              stepIndex={stepIndex}
            />
          )}
          {showGenericLinkInput && (
            <CarrotPlanLinkInput name={createInputNameWithPrefix(FieldName.url)} stepUrl={stepUrl} />
          )}
        </Box>
      </Box>
    </Box>
  )
}
